import React, { useEffect, useState } from "react";
import { Calendar, X } from "lucide-react";
import useNotification from "../../hooks/cs/useNotification";
import { useInfiniteScroll } from "../../hooks/common/useInfiniteScroll";
import LoadingSpinner from "../common/LoadingSpinner";
import { Notification } from "../../types/cs";

const NotificationSection = () => {
  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);
  const {
    notifications,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
    refresh,
  } = useNotification();
  const [showEndMessage, setShowEndMessage] = useState(false);

  useEffect(() => {
    if (!hasMore && notifications.length > 0 && !isLoading && !isLoadingMore) {
      setShowEndMessage(true);

      const timer = setTimeout(() => {
        setShowEndMessage(false);
      }, 3000);

      return () => clearTimeout(timer);
    } else {
      setShowEndMessage(false);
    }
  }, [hasMore, notifications.length, isLoading, isLoadingMore]);

  useInfiniteScroll({
    hasMore,
    isLoading: isLoadingMore,
    loadMore,
    threshold: 200,
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).replace(/\. /g, '.').replace(/\.$/, '');
  };

  const formatDescription = (description: string) => {
    return description.split("\n\n").map((paragraph, index) => (
      <div key={index} className={index > 0 ? "mt-5" : ""}>
        {paragraph.split("\n").map((line, lineIndex) => (
          <span key={lineIndex}>
            {line}
            {lineIndex < paragraph.split("\n").length - 1 && <br />}
          </span>
        ))}
      </div>
    ));
  };

  const getNotificationNumber = (index: number) => {
    return notifications.length - index;
  };

  if (isLoading) {
    return <LoadingSpinner message="공지사항을 불러오는 중..." size="lg" />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 text-lg">{error}</p>
        <button
          onClick={refresh}
          className="mt-4 px-6 py-2 bg-gradient-to-r from-teal-500 via-green-500 to-emerald-500 text-white rounded-lg hover:from-teal-600 hover:via-green-600 hover:to-emerald-600 transition-all duration-200"
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="text-left py-4 px-4 font-medium w-20">번호</th>
              <th className="text-left py-4 px-4 font-medium">제목</th>
              <th className="text-left py-4 px-4 font-medium w-32">작성일</th>
            </tr>
          </thead>
          <tbody>
            {notifications.map((notification, index) => (
              <tr
                key={notification.id}
                className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <td className="py-4 px-4 text-gray-800 text-center">
                  {getNotificationNumber(index)}
                </td>
                <td className="py-4 px-4">
                  <button
                    onClick={() => setSelectedNotification(notification)}
                    className="text-gray-800 hover:underline text-left font-semibold"
                  >
                    {notification.title}
                  </button>
                </td>
                <td className="py-4 px-4 text-gray-600 text-center">
                  {formatDate(notification.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3">
        {notifications.map((notification, index) => (
          <div
            key={notification.id}
            onClick={() => setSelectedNotification(notification)}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <div className="flex items-start justify-between mb-2">
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                No. {getNotificationNumber(index)}
              </span>
            </div>
            <h3 className="font-medium text-teal-600 mb-2 line-clamp-2">
              {notification.title}
            </h3>
            <div className="flex items-center text-xs text-gray-500">
              <Calendar className="w-3 h-3 mr-1" />
              {formatDate(notification.created_at)}
            </div>
          </div>
        ))}
      </div>

      {isLoadingMore && (
        <div className="flex justify-center py-8">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-teal-500 border-t-transparent"></div>
            <span className="text-gray-600">
              더 많은 공지사항을 불러오는 중...
            </span>
          </div>
        </div>
      )}

      {notifications.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">등록된 공지사항이 없습니다.</p>
        </div>
      )}

      {selectedNotification && (
        <div className="fixed -inset-6 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 mx-6 md:mx-0">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between px-4 md:px-8 pt-6 pb-4 border-b relative">
              <div className="flex flex-col gap-2 flex-1 pr-8">
                <h2 className="text-lg md:text-2xl font-bold">
                  {selectedNotification.title}
                </h2>
                <div className="flex items-center text-gray-500 text-sm md:text-lg">
                  <Calendar className="w-4 h-4 mr-2" />
                  {formatDate(selectedNotification.created_at)}
                </div>
              </div>
              <button
                onClick={() => setSelectedNotification(null)}
                className="absolute top-6 right-4 md:right-8 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="w-6 h-6 md:w-8 md:h-8" />
              </button>
            </div>

            <div className="px-4 md:px-8 py-6 overflow-y-auto scrollbar-hover-stable max-h-[700px]">
              {selectedNotification.reference_image && (
                <div className="mb-6">
                  <img
                    src={selectedNotification.reference_image}
                    alt="공지사항 이미지"
                    className="w-full max-w-md mx-auto rounded-lg"
                  />
                </div>
              )}

              <div className="prose max-w-none">
                <div
                  className="text-gray-700 text-sm md:text-xl"
                  style={{ lineHeight: "1.6" }}
                >
                  {formatDescription(selectedNotification.description)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSection;
